'use client'

import React, { useRef } from 'react'
import { motion, useMotionValue, useReducedMotion, useSpring } from 'framer-motion'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'

type ButtonProps = React.ComponentProps<typeof Button>

export function MagneticButton({ children, className, ...props }: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const shouldReduceMotion = useReducedMotion()
  const x = useMotionValue(0)
  const y = useMotionValue(0)
  const springX = useSpring(x, { stiffness: 150, damping: 15, mass: 0.1 })
  const springY = useSpring(y, { stiffness: 150, damping: 15, mass: 0.1 })

  const handlePointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (shouldReduceMotion) return
    const node = buttonRef.current
    if (!node) return
    const { clientX, clientY } = e
    const { left, top, width, height } = node.getBoundingClientRect()

    // Calculate distance from center
    const x = (clientX - (left + width / 2)) * 0.35 // Multiplier controls magnetic strength
    const y = (clientY - (top + height / 2)) * 0.35

    springX.set(x)
    springY.set(y)
  }

  const handlePointerLeave = () => {
    springX.set(0)
    springY.set(0)
  }

  if (shouldReduceMotion) {
    return (
      <Button ref={buttonRef} className={cn('relative overflow-hidden', className)} {...props}>
        {children}
      </Button>
    )
  }

  return (
    <motion.div style={{ x: springX, y: springY }}>
      <Button
        ref={buttonRef}
        className={cn('relative overflow-hidden', className)}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        {...props}
      >
        {children}
      </Button>
    </motion.div>
  )
}
